import CustomEditorToolbar from "./CustomEditorToolbar";
import { useEffect, useRef, useState } from "react";

const CustomEditorForContract = ({
  data,
  editorContentRefs,
  editorContainerRefs,
  uniqueKey,
  index,
  placeholderDropdownOpen,
  setPlaceholderDropdownOpen,
  setFormDetails,
  hideToolBar,
  contractData
}) => {
  // Track the last data.text value to detect actual changes
  const lastDataTextRef = useRef(data.text);
  // Track if we're in the middle of a programmatic update (to prevent ref callback interference)
  const isUpdatingRef = useRef(false);
  const [rerender, setReRender] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setReRender(true);
    }, 300);
  }, []);
  return (
    <>
      <div
        ref={(el) => {
          if (el) {
            editorContainerRefs.current[uniqueKey] = el;
          }
        }}
        style={{ position: "relative" }}
      >
        {/* Custom Toolbar */}
        {hideToolBar ? null : (
          <CustomEditorToolbar
            editorContainerRef={editorContainerRefs.current[uniqueKey]}
            editorContentRef={editorContentRefs.current[uniqueKey]}
            editorKey={uniqueKey}
            placeholderDropdownOpen={placeholderDropdownOpen}
            setPlaceholderDropdownOpen={setPlaceholderDropdownOpen}
            key={rerender}
            contractData={contractData}
          />
        )}
        {/* Editor Content Area */}
        <div
          contentEditable="true"
          className="pell-content"
          suppressContentEditableWarning
          data-editor-key={uniqueKey}
          id={`editor-content-${uniqueKey}`}
          onInput={(e) => {
            // Set flag to prevent ref callback from interfering
            isUpdatingRef.current = true;
            setFormDetails(index, e.currentTarget.innerHTML, true);
            setTimeout(() => {
              isUpdatingRef.current = false;
            }, 100);
          }}
          style={{
            padding: "14px 0",
            minHeight: "160px",
            fontSize: "14px",
            fontWeight: "600",
            lineHeight: "1.43",
            color: "#102c42",
            fontFamily: "Poppins",
            border: "0",
            borderBottom: "1px solid #dfdfe4",
            height: "auto",
          }}
          ref={(el) => {
            editorContentRefs.current[uniqueKey] = el;
            if (el && el.innerHTML !== data.text) {
              // Skip reset if we're in the middle of a programmatic update
              if (
                isUpdatingRef.current ||
                el.getAttribute("data-updating") === "true"
              ) {
                return;
              }

              // Normalize HTML for comparison ,ignore minor differences
              const normalizeHTML = (html) => {
                return html
                  .replace(/&nbsp;/g, " ")
                  .replace(/\s+/g, " ")
                  .replace(/<br\s*\/?>/gi, "<br>")
                  .replace(/<\/br>/gi, "")
                  .trim();
              };

              const normalizedCurrent = normalizeHTML(el.innerHTML);
              const normalizedData = normalizeHTML(data.text);
              const isInitialMount =
                !lastDataTextRef.current ||
                el.innerHTML === "" ||
                el.innerHTML.trim() === "";

              // Check if data.text actually changed not just a re-render
              const dataTextChanged = lastDataTextRef.current !== data.text;

              const isEditorFocused = document.activeElement === el;
              const selection = window.getSelection();
              const isSelectionInThisEditor =
                selection &&
                selection.rangeCount > 0 &&
                el.contains(selection.anchorNode);

              if (dataTextChanged) {
                lastDataTextRef.current = data.text;
              }

              const shouldReset =
                normalizedCurrent !== normalizedData &&
                (isInitialMount ||
                  dataTextChanged ||
                  isEditorFocused ||
                  isSelectionInThisEditor);

              if (shouldReset) {
                let textOffset = null;

                if (isSelectionInThisEditor || isEditorFocused) {
                  if (isSelectionInThisEditor) {
                    const range = selection.getRangeAt(0);
                    // Calculate text offset from start of editor
                    const preRange = document.createRange();
                    preRange.selectNodeContents(el);
                    preRange.setEnd(range.startContainer, range.startOffset);
                    textOffset = preRange.toString().length;
                  } else if (isEditorFocused) {
                    // Editor is focused but no selection - place cursor at end
                    const preRange = document.createRange();
                    preRange.selectNodeContents(el);
                    textOffset = preRange.toString().length;
                  }
                }

                // Reset innerHTML
                el.innerHTML = data.text;

                // Restore cursor position after reset using text offset
                if (
                  textOffset !== null &&
                  (isSelectionInThisEditor || isEditorFocused)
                ) {
                  requestAnimationFrame(() => {
                    try {
                      // Verify this is still the correct editor
                      if (
                        el &&
                        document.getElementById(
                          `editor-content-${uniqueKey}`
                        ) === el
                      ) {
                        // Focus the editor first if it was focused before
                        if (isEditorFocused && document.activeElement !== el) {
                          el.focus();
                        }

                        const newSelection = window.getSelection();
                        if (newSelection) {
                          // Find the equivalent text position in the new DOM
                          const walker = document.createTreeWalker(
                            el,
                            NodeFilter.SHOW_TEXT,
                            null
                          );

                          let currentOffset = 0;
                          let targetNode = null;
                          let targetOffset = 0;

                          let node;
                          while ((node = walker.nextNode())) {
                            const nodeLength = node.textContent.length;
                            if (currentOffset + nodeLength >= textOffset) {
                              targetNode = node;
                              targetOffset = textOffset - currentOffset;
                              break;
                            }
                            currentOffset += nodeLength;
                          }

                          if (targetNode) {
                            const newRange = document.createRange();
                            newRange.setStart(
                              targetNode,
                              Math.min(
                                targetOffset,
                                targetNode.textContent.length
                              )
                            );
                            newRange.collapse(true);
                            newSelection.removeAllRanges();
                            newSelection.addRange(newRange);
                          } else {
                            // Fallback: place at end
                            const newRange = document.createRange();
                            newRange.selectNodeContents(el);
                            newRange.collapse(false);
                            newSelection.removeAllRanges();
                            newSelection.addRange(newRange);
                          }
                        }
                      }
                    } catch (e) {
                      // Error restoring cursor - silently fail
                    }
                  });
                }
              }
            }
          }}
          data-gramm={false}
        />
      </div>
      {data.error && <span className="error-msg">{data.error}</span>}
    </>
  );
};
export default CustomEditorForContract;
